/**
 * Vendor "Settings" page (`/vendor/app/settings`).
 *
 * Two self-contained sections:
 *  - Notification preferences — SMS / email / push / marketing toggles that
 *    auto-save to the caller's User row via `/user/profile` (the vendor's
 *    onboarding token carries their phone, which the backend resolves to that
 *    same User row, so a vendor edits their own delivery prefs here).
 *  - Appearance — light/dark theme toggle (client-only, persisted by the store).
 *
 * Notifications themselves live on a separate page (`/vendor/app/notifications`);
 * this page is only about *preferences* and app settings.
 */
import { useEffect, useState } from 'react';
import { Loader2, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { use_theme_store } from '@/lib/theme-store';
import { use_user_profile, use_update_user_profile } from '@/features/user-profile/hooks';
import { AccountDeletionCard } from '@/components/account-deletion-card';

type NotifKey = 'notify_sms' | 'notify_email' | 'notify_push' | 'marketing_opt_in';

const NOTIF_ROWS: { key: NotifKey; label: string; hint: string }[] = [
  { key: 'notify_sms', label: 'SMS', hint: 'Booking & order updates by text' },
  { key: 'notify_email', label: 'Email', hint: 'Receipts, payouts and summaries' },
  { key: 'notify_push', label: 'Push', hint: 'Alerts on your phone' },
  { key: 'marketing_opt_in', label: 'Offers & tips', hint: 'Occasional product news' },
];

function NotificationPrefsCard() {
  const profile_q = use_user_profile();
  const update = use_update_user_profile();
  const p = profile_q.data;

  // Local mirror so toggles flip instantly; the PATCH persists in the background.
  const [prefs, set_prefs] = useState<Record<NotifKey, boolean>>({
    notify_sms: true,
    notify_email: true,
    notify_push: true,
    marketing_opt_in: false,
  });
  useEffect(() => {
    if (!p) return;
    set_prefs({
      notify_sms: p.notify_sms,
      notify_email: p.notify_email,
      notify_push: p.notify_push,
      marketing_opt_in: p.marketing_opt_in,
    });
  }, [p]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Notification preferences</CardTitle>
      </CardHeader>
      <CardContent>
        {profile_q.isLoading ? (
          <div className="flex flex-col gap-3">
            {NOTIF_ROWS.map((r) => (
              <Skeleton key={r.key} className="h-12 rounded-card" />
            ))}
          </div>
        ) : profile_q.isError ? (
          <ErrorState
            title="Couldn't load preferences"
            description="Please try again."
            action={
              <Button variant="secondary" size="sm" onClick={() => profile_q.refetch()}>
                Retry
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col divide-y divide-border">
            {NOTIF_ROWS.map((r) => (
              <label
                key={r.key}
                className="flex cursor-pointer items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <span>
                  <span className="block text-sm font-semibold text-foreground">{r.label}</span>
                  <span className="block text-xs text-muted-foreground">{r.hint}</span>
                </span>
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded border-input accent-primary"
                  data-testid={`toggle-${r.key}`}
                  checked={prefs[r.key]}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    set_prefs((prev) => ({ ...prev, [r.key]: checked }));
                    update.mutate({ [r.key]: checked });
                  }}
                />
              </label>
            ))}
          </div>
        )}
        {update.isPending && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AppearanceCard() {
  const dark = use_theme_store((s) => s.dark);
  const toggle_theme = use_theme_store((s) => s.toggle);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Appearance</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Theme</p>
            <p className="text-xs text-muted-foreground">{dark ? 'Dark mode' : 'Light mode'}</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="gap-2"
            onClick={toggle_theme}
            data-testid="settings-theme-toggle"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {dark ? 'Switch to light' : 'Switch to dark'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function VendorSettingsPage() {
  return (
    <div data-testid="vendor-settings" className="flex flex-col gap-6">
      <SectionHeader
        title="Settings"
        description="Choose how we reach you and how the workspace looks."
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <NotificationPrefsCard />
        <AppearanceCard />
        {/* Last, and on its own row at width — it is the one control here that ends things. */}
        <AccountDeletionCard role="vendor" />
      </div>
    </div>
  );
}
